import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { donationSchemasAPI } from '../services/api';
import { colors } from '../theme/modernTheme';
import { showError } from '../utils/toast';

export default function DonationSchemaDialog({
  open,
  onClose,
  schema,
  onSuccess,
  initialName = '',
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (open) {
      if (schema) {
        setFormData({
          name: schema.name || '',
          description: schema.description || '',
        });
      } else {
        setFormData({
          name: initialName || '',
          description: '',
        });
      }
    }
  }, [schema, open, initialName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling to parent forms
    setLoading(true);
    setFieldErrors({});

    try {
      let response;
      if (schema) {
        response = await donationSchemasAPI.update(schema.id, formData);
      } else {
        response = await donationSchemasAPI.create(formData);
      }
      // Close dialog first, then trigger refresh
      onClose();
      // Small delay to ensure dialog closes before refresh
      setTimeout(() => {
        onSuccess(response?.data);
      }, 100);
    } catch (err) {
      console.error('[DonationSchemaDialog] Error creating/updating schema:', err);
      if (err.error?.code === 'VALIDATION_ERROR') {
        const errors = err.error.details?.field_errors || {};
        setFieldErrors(errors);
        // Don't show toast - field errors are displayed below each field
      } else {
        showError(err?.error?.message || 'Failed to save donation schema');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disablePortal={false}
      PaperProps={{
        sx: {
          borderRadius: '4px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      }}
    >
      <form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}>
        <DialogTitle
          sx={{
            pb: 1,
            borderBottom: `1px solid ${colors.border.main}`,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              textTransform: 'capitalize',
              fontWeight: 700,
              color: colors.text.primary,
              fontSize: '1.1rem',
            }}
          >
            {schema ? 'Edit Donation Schema' : 'Create New Donation Schema'}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 4 }}>
            <TextField
              fullWidth
              label="Schema Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              autoFocus
              error={!!fieldErrors.name}
              helperText={fieldErrors.name || `${formData.name.length}/100`}
              inputProps={{
                maxLength: 100,
              }}
              FormHelperTextProps={{
                sx: {
                  marginLeft: 0,
                  marginRight: 0,
                  textAlign: fieldErrors.name ? 'left' : 'right',
                },
              }}
            />

            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
              error={!!fieldErrors.description}
              helperText={fieldErrors.description || `${formData.description.length}/1000`}
              inputProps={{
                maxLength: 1000,
              }}
              FormHelperTextProps={{
                sx: {
                  marginLeft: 0,
                  marginRight: 0,
                  textAlign: fieldErrors.description ? 'left' : 'right',
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            py: 2,
            px: 1,
            borderTop: `1px solid ${colors.border.main}`,
            gap: 1,
          }}
        >
          <Button
            onClick={onClose}
            disabled={loading}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderColor: colors.error.main,
              color: colors.error.main,
              borderRadius: '4px',
              '&:hover': {
                borderColor: colors.error.dark,
                backgroundColor: 'rgba(220, 38, 38, 0.04)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              backgroundColor: colors.primary.main,
              '&:hover': {
                backgroundColor: colors.primary.dark,
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
